"use client";

import { FormEvent, useState } from "react";

export interface ShiftFormValues {
  name: string;
  code: string;
  start_time: string;
  end_time: string;
  description: string;
  isActive: boolean;
}

interface ShiftCreateModalProps {
  onClose: () => void;
  onSubmit: (values: ShiftFormValues) => Promise<boolean>;
  isSubmitting: boolean;
  initialValues?: ShiftFormValues;
  mode: "create" | "edit";
}

const defaultValues: ShiftFormValues = {
  name: "",
  code: "",
  start_time: "08:00",
  end_time: "17:00",
  description: "",
  isActive: true,
};

const ShiftCreateModal: React.FC<ShiftCreateModalProps> = ({
  onClose,
  onSubmit,
  isSubmitting,
  initialValues,
  mode,
}) => {
  const [form, setForm] = useState<ShiftFormValues>({ ...defaultValues, ...(initialValues ?? {}) });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const titleText = mode === "create" ? "Thêm mới ca làm việc" : "Cập nhật ca làm việc";
  const submitText = mode === "create" ? "Thêm mới" : "Cập nhật";

  const handleChange = <K extends keyof ShiftFormValues>(key: K, value: ShiftFormValues[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) {
      newErrors.name = "Vui lòng nhập tên ca làm việc.";
    }
    if (!form.code.trim()) {
      newErrors.code = "Vui lòng nhập mã ca làm việc.";
    }
    if (!form.start_time) {
      newErrors.start_time = "Vui lòng chọn thời gian bắt đầu.";
    }
    if (!form.end_time) {
      newErrors.end_time = "Vui lòng chọn thời gian kết thúc.";
    }
    if (form.start_time >= form.end_time) {
      newErrors.end_time = "Thời gian kết thúc phải sau thời gian bắt đầu.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validate()) return;

    const success = await onSubmit(form);
    if (success) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-8 shadow-2xl">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-slate-800">{titleText}</h3>
          <button onClick={onClose} type="button" className="text-slate-400 hover:text-slate-600 text-2xl">
            ✕
          </button>
        </div>

        <form className="mt-6 grid grid-cols-2 gap-x-4 gap-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Tên ca làm việc <span className="text-red-500">*</span>
            </label>
            <input
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className="mt-1 w-full rounded-xl border px-3 py-2"
              placeholder="VD: Ca sáng, Ca hành chính"
            />
            {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">
              Mã ca <span className="text-red-500">*</span>
            </label>
            <input
              value={form.code}
              onChange={(e) => handleChange("code", e.target.value)}
              className="mt-1 w-full rounded-xl border px-3 py-2"
              placeholder="VD: CA_SANG"
            />
            {errors.code && <p className="mt-1 text-xs text-red-500">{errors.code}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">
              Thời gian bắt đầu <span className="text-red-500">*</span>
            </label>
            <input type="time" value={form.start_time} onChange={(e) => handleChange("start_time", e.target.value)} className="mt-1 w-full rounded-xl border px-3 py-2" />
            {errors.start_time && <p className="mt-1 text-xs text-red-500">{errors.start_time}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Thời gian kết thúc <span className="text-red-500">*</span>
            </label>
            <input type="time" value={form.end_time} onChange={(e) => handleChange("end_time", e.target.value)} className="mt-1 w-full rounded-xl border px-3 py-2" />
            {errors.end_time && <p className="mt-1 text-xs text-red-500">{errors.end_time}</p>}
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-slate-700">Mô tả</label>
            <textarea value={form.description} onChange={(e) => handleChange("description", e.target.value)} className="mt-1 w-full rounded-xl border px-3 py-2" rows={3} placeholder="Nhập mô tả (nếu có)"></textarea>
          </div>


          <div className="col-span-2 flex items-center justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="rounded-full border px-5 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">
              Hủy
            </button>
            <button type="submit" disabled={isSubmitting} className="rounded-full bg-green-600 px-6 py-2 text-sm font-semibold text-white shadow hover:bg-green-700 disabled:opacity-60">
              {isSubmitting ? "Đang lưu..." : submitText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShiftCreateModal;